import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRightIcon } from 'lucide-react';
import { Reveal } from '../components/Reveal';
import { GridRails } from '../components/GridRails';
import { GridDivider } from '../components/GridDivider';
import { FillLabel } from '../components/FillLabel';
import { MaskedArt } from '../components/Logo';
import type { CreditDoc } from '../data/credits';
import { creditDocs } from '../data/credits';
import { usePageMeta } from '../hooks/usePageMeta';
type Props = {
  doc: CreditDoc;
};
const inner = 'relative z-10 mx-auto max-w-[1720px] px-5 md:px-16 lg:px-24';
const label = 'font-accent text-[11px] uppercase tracking-[0.08em]';

/** Credits page for the agency behind the brand and website. */
export function Credits({
  doc
}: Props) {
  usePageMeta({
    title: `Credits — ${doc.name} | KAYS & KAYS`,
    description: `${doc.name} created the KAYS & KAYS brand and website: strategy, concept, design and development.`
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [doc]);
  return <>
      {/* Navy band under the header */}
      <section className="relative bg-navy pb-16 pt-[180px] text-cream md:pb-20 md:pt-[220px]" data-id="element-446" data-mp="126w0z6jw75il">
        <div className={inner} data-id="element-447" data-mp="o74esij82iti">
          <Reveal data-id="element-448" data-mp="o7wd4147eunh">
            <p className={`${label} flex items-center gap-3 text-cream/70`} data-id="element-449" data-mp="oaietk4eyfxs">
              <span aria-hidden="true" className="inline-block h-[6px] w-[6px] bg-cream" data-id="element-450" data-mp="s7y49gcbc5oi" />
              Credits
            </p>
            {doc.logo && <a href={doc.url} target="_blank" rel="noopener noreferrer" aria-label={`${doc.name} — ${doc.urlLabel}`} className="mt-10 inline-block text-cream opacity-90 transition-opacity duration-200 hover:opacity-100" data-id="element-451" data-mp="j9clpl6ucqnq">
                <MaskedArt src={doc.logo.src} className={`${doc.logo.aspect} h-[44px] md:h-[60px]`} data-id="element-452" data-mp="3rq7eg7txcuo3" />
              </a>}
            <h1 className="mt-6 font-display text-[44px] leading-[0.98] tracking-[-0.035em] md:text-[72px]" data-id="element-453" data-mp="ko355vtyxf4">
              Made by <span className="font-editorial italic tracking-[-0.015em]" data-id="element-454" data-mp="1xh0xhjlwtb8k">{doc.name}</span>
            </h1>
            {doc.descriptor && <p className="mt-4 text-[16px] text-cream/80" data-id="element-455" data-mp="1sh66zh61qno1">{doc.descriptor}</p>}
            <p className="mt-6 max-w-[560px] text-[14.5px] leading-[1.55] text-cream/65" data-id="element-456" data-mp="1ogm8a95trbzs">{doc.intro}</p>
            <a href={doc.url} target="_blank" rel="noopener noreferrer" className="btn-fill btn-fill-light mt-10 inline-flex h-[48px] items-center gap-3 whitespace-nowrap border border-cream/30 px-6 font-accent text-[11px] uppercase tracking-[0.08em] text-cream/90" data-id="element-457" data-mp="2l6d6aidbrhac">
              <FillLabel data-id="element-458" data-mp="2762bx5ct3kkf">{`Visit ${doc.urlLabel}`}</FillLabel>
              <ArrowUpRightIcon aria-hidden="true" strokeWidth={1.25} className="relative z-[1] h-4 w-4" data-id="element-459" data-mp="10qi3hygwh677" />
            </a>
          </Reveal>
        </div>
      </section>

      <section className="relative bg-white text-ink" data-id="element-460" data-mp="vtrolegu0gnd">
        <GridRails data-id="element-461" data-mp="37g3cfhnithpy" />
        <div className={`${inner} grid gap-12 px-9 py-16 md:grid-cols-[220px_1fr] md:gap-20 md:py-24`} data-id="element-462" data-mp="37glfax4ouu6u">
          <aside className="md:sticky md:top-12 md:self-start" data-id="element-463" data-mp="3ced7ls98wibk">
            <Reveal data-id="element-464" data-mp="3genqg5i4z37u">
              <p className={`${label} text-ink/50`} data-id="element-465" data-mp="3ge5memp8fkpd">Agency</p>
              {doc.logo ? <MaskedArt src={doc.logo.src} className={`${doc.logo.aspect} mt-3 h-[22px] text-ink`} data-id="element-466" data-mp="3l78gqx1u2y6m" /> : <p className="mt-2 text-[13px] text-ink/80" data-id="element-467" data-mp="3q7mffohjovrz">{doc.name}</p>}
              {doc.descriptor && <p className="mt-1 text-[13px] text-ink/60" data-id="element-468" data-mp="3v6fu3k8kistq">{doc.descriptor}</p>}
              <a href={doc.url} target="_blank" rel="noopener noreferrer" className="link-soft mt-3 inline-block text-[13px] text-ink" data-id="element-469" data-mp="3z3bd0s1e7bp">
                {doc.urlLabel}
              </a>

              <p className={`${label} mt-10 text-ink/50`} data-id="element-470" data-mp="29qi8779xtu9u">Credits</p>
              <ul className="mt-4 space-y-2 text-[13px]" data-id="element-471" data-mp="1eh3gzzqvf7rd">
                {creditDocs.map(d => <li key={d.slug} data-id="element-472" data-mp="1afi6um5q17kv">
                    {d.slug === doc.slug ? <span className="flex items-center gap-2 text-ink" data-id="element-473" data-mp="1ahun8uvtz9vt">
                        <span aria-hidden="true" className="inline-block h-[5px] w-[5px] bg-ink" data-id="element-474" data-mp="1oehy81kmc0kq" />
                        {d.name}
                      </span> : <Link to={`/${d.slug}`} className="link-soft text-ink/70" data-id="element-475" data-mp="vrdppmmbh447">
                        {d.name}
                      </Link>}
                  </li>)}
              </ul>
            </Reveal>
          </aside>

          <article className="max-w-[680px]" data-id="element-476" data-mp="10pymtd69v7of">
            <Reveal data-id="element-477" data-mp="10o45s9y4fzpm">
              <h2 className={`${label} text-ink/80`} data-id="element-478" data-mp="10nm1qr57wh75">Scope of work</h2>
              <p className="mt-4 font-display text-[26px] leading-[1.2] tracking-[-0.02em] text-ink md:text-[32px]" data-id="element-479" data-mp="cyjixfyukib2">
                Strategy, concept, design and development of the whole KAYS &amp; KAYS brand and this website.
              </p>
            </Reveal>

            <ol className="mt-12" data-id="element-480" data-mp="2ofmo59wjq68g">
              {doc.roles.map((r, i) => <li key={r.title} className="border-t border-mist-line py-7" data-id="element-481" data-mp="2anomfk55zcmb" data-mpi={i}>
                  <Reveal className="grid gap-2 md:grid-cols-[60px_1fr]" data-id="element-482" data-mp="2allsip0w4zcb">
                    <span className={`${label} tabular-nums text-ink/40`} data-id="element-483" data-mp="2epteiefys16u">{String(i + 1).padStart(2, '0')}</span>
                    <div data-id="element-484" data-mp="32fnuv5zhs7vu">
                      <h3 className="text-[18px] font-medium tracking-[-0.01em] text-ink" data-id="element-485" data-mp="37dyz9170pq6o">{r.title}</h3>
                      <p className="mt-2 text-[14.5px] leading-[1.6] text-ink/70" data-id="element-486" data-mp="2teg5xeiv8jef">{r.body}</p>
                    </div>
                  </Reveal>
                </li>)}
            </ol>

            <Reveal className="border-t border-mist-line pt-7" data-id="element-487" data-mp="1hzapl7ptamcc">
              <p className="text-[14.5px] leading-[1.6] text-ink/70" data-id="element-488" data-mp="1hxpvfj7et5f0">
                Learn more about {doc.name} at{' '}
                <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-ink underline decoration-ink/30 underline-offset-[3px] transition-[text-decoration-color] duration-200 hover:decoration-ink" data-id="element-489" data-mp="3ona07w735pfe">
                  {doc.urlLabel}
                </a>
                .
              </p>
            </Reveal>

            <p className="mt-14 flex flex-wrap gap-x-8 gap-y-3 text-[13px] text-ink/50" data-id="element-490" data-mp="35wifxzlk8em8">
              <Link to="/imprint" className="link-soft no-arrow text-ink/70" data-id="element-491" data-mp="30xp7ill4rdsx">
                ← Imprint
              </Link>
              <Link to="/" className="link-soft no-arrow text-ink/70" data-id="element-492" data-mp="2vxuf2dlpig5r">
                Back to KAYS &amp; KAYS
              </Link>
            </p>
          </article>
        </div>
      </section>
      <GridDivider data-id="element-493" data-mp="u7iy2tlusp8u" />
    </>;
}